import { cx } from '@orn/shared-web/utils/cx';

import { formatCalendarDate, toCurrency } from '../../../utils/formatting';
import Entity from '../../entity';

import type { EntityAvatarFragment } from '../../entity/EntityAvatar/__generated__/EntityAvatar.gql';
import type { CalendarDate } from '@internationalized/date';

/** Why a paired transaction is shown at all — the copy behind the "Paired with" info glyph. */
export const TRANSACTION_PAIR_TOOLTIP =
  'This is a transfer between your accounts. Because the added and subtracted amounts are equal, it nets out to zero total spending.';

export interface ITransactionPairValueProps {
  pairedTransaction: {
    amount: string;
    date: CalendarDate;
    /** Drawn as the leading avatar; a paired transaction whose account is gone shows date and amount alone. */
    financialAccount?: EntityAvatarFragment | null;
  };
  /**
   * Typography of the date and amount.
   * @default 'font-semibold'
   */
  textClassName?: string;
}

/**
 * The value cell for the other side of a transfer: the paired transaction's
 * bank avatar, its date, and its amount signed from that transaction's point
 * of view — a negative amount is money in, so it reads `+` in green.
 */
const TransactionPairValue = ({ pairedTransaction, textClassName = 'font-semibold' }: ITransactionPairValueProps) => {
  const amountNumber = Number(pairedTransaction.amount);
  const isCredit = amountNumber < 0;
  const formattedAmount = `${isCredit ? '+' : '-'}${toCurrency(Math.abs(amountNumber))}`;

  return (
    <div className={cx('text-primary text-md flex min-w-0 items-center gap-2', textClassName)}>
      {pairedTransaction.financialAccount && <Entity.Avatar entity={pairedTransaction.financialAccount} size="xs" />}
      <span className="whitespace-nowrap">{formatCalendarDate(pairedTransaction.date, 'medium')} •</span>
      <span className={cx('whitespace-nowrap', isCredit ? 'text-success-primary' : 'text-error-primary')}>
        {formattedAmount}
      </span>
    </div>
  );
};

export default TransactionPairValue;
